import { type FC, useEffect, useState } from 'react';
import { UnControlled as CodeMirror } from 'react-codemirror2';
import type codemirror from 'codemirror';
import { useCodeMirrorStrictModeHandlers } from '@editor2/hooks/useCodeMirrorStrictModeHandlers';
import { useFlushSyncCallback } from '@editor2/hooks/useFlushSyncCallback';

import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/yaml/yaml.js';

import 'codemirror/addon/display/autorefresh.js';

import 'codemirror/addon/fold/foldcode.js';
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/addon/fold/foldgutter.js';
import 'codemirror/addon/fold/indent-fold.js';

import { updateYamlConfig } from '@editor2/reducers/editor/editorSlice';
import { yamlEditor } from '../styles';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';

interface Props {
	yamlConfig: string;
	canEditResourceConfig: boolean;
	courseId: number;
}

const YamlEditor: FC<Props> = (props) => {
	const { yamlConfig, canEditResourceConfig, courseId } = props;

	const debouncedDispatch = useDebouncedDispatch();
	const { wrapperRef, editorRef, handleEditorDidMount, handleEditorWillUnmount } =
		useCodeMirrorStrictModeHandlers();

	const codeMirrorOptions = {
		mode: 'yaml',
		lineNumbers: true,
		autoRefresh: true,
		lineWrapping: true,
		foldGutter: true,
		gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
		...(!canEditResourceConfig && { readOnly: 'nocursor' }),
		extraKeys: {
			Tab: false,
			'Shift-Tab': false,
			'Shift-Alt-F': 'indentAuto',
			'Shift-Option-F': 'indentAuto'
		},
		// Configure history for better undo/redo preservation
		undoDepth: 300,
		historyEventDelay: 2000
	} satisfies codemirror.EditorConfiguration;

	const [config, setConfig] = useState(yamlConfig);

	useEffect(() => {
		if (editorRef.current && yamlConfig !== editorRef.current.getValue()) {
			setConfig(yamlConfig);
		}
	}, [yamlConfig, editorRef]);

	const handleChange = (value: string) => {
		debouncedDispatch(updateYamlConfig({ yamlConfig: value, courseId }));
	};

	// Wrap handleChange with flushSync for synchronous updates
	const syncHandleChange = useFlushSyncCallback(handleChange);

	return (
		<div className={yamlEditor}>
			<CodeMirror
				className="codeBlock"
				ref={wrapperRef}
				value={config}
				options={codeMirrorOptions}
				onChange={(editor, data, value) => syncHandleChange(value)}
				editorDidMount={handleEditorDidMount}
				editorWillUnmount={handleEditorWillUnmount}
			/>
		</div>
	);
};

export default YamlEditor;
